/* eslint-disable default-case */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import InputField from '../InputField';
import Grid from '../Grid';
import GridItem from '../GridItem';

import isString from '../../Utils/isString';
import useDebounce from '../../Utils/hooks/useDebounce';
import makeClassName from '../../Utils/makeClassName';

import styles, { mainCls } from './styles';
import createStyle from '../../Utils/createStyle';

const useStyles = createStyle(styles);

const googleBtnNameList = [
  'via ',
  'viale ',
  'piazza ',
  'corso ',
  'strada ',
  'vicolo ',
];

const isValidToQuery = (val) => val && val.length >= 2;
const getInitialData = (data, preview) =>
  isString(data) || !preview ? [] : data;

/**
 * Autocomplete input. Not specified props will be forwarded to the inner `InputField` component
 */
export default function AutocompleteField({
  type,
  value,
  className,
  itemClassName,
  itemsClassName,
  placeholderBack,
  onChange,
  onChangeGoogle,
  onFocus,
  onEnd,
  onHighlighted,
  cancel,
  getDisplayName,
  getNameSelected,
  itemToValue,
  itemsAbs,
  shortOnFocus,
  data,
  dataProcess,
  emptyState,
  preItems,
  postItems,
  itemsTarget,
  children,
  isValid,
  noHeightLimit,
  previewData,
  allowAny,
  showCancel,
  debounce,
  fetcher,
  themeItems,
  shouldClearOnSelect,
  showCloseIfQuery,
  withGoogleButton,
  getGoogleFromLHSuggestionsRaw,
  queryBuilderProps,
  uuidv4,
  highlightedValueCls,
  flatSelectedEffect,
  cameFrom,
  preventBlurFromInput = false,
  stopClickOutside = false,
  ...props
}) {
  const cls = useStyles();
  const [selected, setSelected] = useState({});
  const [highlighted, setHighlighted] = useState({});
  const [items, setItems] = useState(getInitialData(data, previewData));
  const [query, setQuery] = useState(null);
  const [isFetching, setFetching] = useState(0);
  const [focused, setFocused] = useState(0);
  const [listType, setListType] = useState('default');
  const [uuid, setUiid] = useState(null);
  const [lastClick, setLastClick] = useState(0);
  const highlightedEl = useRef(null);
  const elRef = useRef(null);
  const dataIsAsync = isString(data);
  const debouncedQuery = dataIsAsync ? useDebounce(query, debounce) : query;
  useEffect(() => {
    setSelected(value);
    setQuery(null);
  }, [value]);

  /**
   * Create beahvior of scrollIntoView every time
   * the highlighted item changes.
   */
  useEffect(() => {
    const { current } = highlightedEl || {};
    // current && current.scrollIntoView({ block: 'nearest' }); // Can't use due to Edge bug.... sooo...

    if (current) {
      const { parentNode } = current;
      const parentT = parentNode.scrollTop;
      const parentH = parentNode.offsetHeight;
      const parentB = parentT + parentH;
      const currentT = current.offsetTop;
      const currentH = current.offsetHeight;
      const currentB = currentT + currentH;
      const isAbove = currentB <= parentT;
      const isBelow = currentT >= parentB;

      isAbove && (parentNode.scrollTop = currentT);
      isBelow && (parentNode.scrollTop = currentT - (parentH - currentH));
    }

    onHighlighted(highlighted, items, query);
  }, [highlighted, items]); // eslint-disable-line

  const clear = () => {
    setQuery(null);
    showCloseIfQuery && setSelected({});
    setListType('default');
    setUiid(null);
    setItems(getInitialData(data, previewData));
  };

  const setSelectedItem = async (item) => {
    /**
     * Try to avoid double click on google button
     * and then double request and double tracking
     */
    if (item.buttonType === 'google') {
      const now = new Date().getTime();
      const timeDiff = now - lastClick;
      if (timeDiff > 2000) {
        setLastClick(now);
        const dq = debouncedQuery.toLowerCase();
        const withAddress = googleBtnNameList.some((p) => dq.startsWith(p));
        trackGTMEvents(
          {
            category: 'QueryBuilder_Locality',
            action: 'TapOnSeeGoogleSuggestion',
            label: withAddress ? 'AddressCopy' : 'DefaultCopy',
          },
          {},
          true
        );
        return showGoogleResults({ isFirstCall: true });
      }
    }
    setSelected(item);
    setHighlighted(item);
    switch (listType) {
      case 'google':
        trackGTMEvents(
          {
            category: 'QueryBuilder_Locality',
            action: 'SelectLocality',
            position: 'GoogleSuggestion',
          },
          {},
          true
        );
        setFetching(1);
        await onChangeGoogle(item, uuid);
        setUiid(null);
        setFetching(0);
        break;

      default:
        if (
          item.buttonType !== 'google' &&
          cameFrom === 'contentLocalitySearchQB'
        ) {
          trackGTMEvents(
            {
              category: 'QueryBuilder_Locality',
              action: 'SelectLocality',
              position: 'CasaSuggestion',
            },
            {},
            true
          );
        }
        onChange(itemToValue(item));
        break;
    }
    shouldClearOnSelect && clear();
    setFocused();
    onEnd();
  };

  const handleClickOutside = (ev) => {
    const { target } = ev;
    const clickInside =
      (elRef.current && elRef.current.contains(target)) ||
      (itemsTarget &&
        itemsTarget.current &&
        itemsTarget.current.contains(target));

    if (!clickInside) {
      if (debouncedQuery === '' && listType !== 'google') {
        setSelectedItem({});
      } else if (allowAny) {
        setSelectedItem(debouncedQuery ? { name: debouncedQuery } : selected);
      } else {
        clear();
      }
      setFocused();
      setTimeout(onEnd, 250);
    }
  };

  useEffect(() => {
    if (!stopClickOutside) {
      const events = ['click'];
      // This was causing an issue on iOS, e.g when someone was trying to click a button outside
      // of the autocomplete, the suggestion ends before the actual trigger of the button
      // getDeviceProperties().mobileOs === 'ios' && events.push('touchstart');
      focused &&
        events.forEach((ev) => document.addEventListener(ev, handleClickOutside));
      return () =>
        events.forEach((ev) =>
          document.removeEventListener(ev, handleClickOutside)
        );
    }
  }, [focused, debouncedQuery, stopClickOutside]); // eslint-disable-line
  // https://github.com/facebook/react/issues/14066

  useEffect(() => {
    const fetchResults = async (val) => {
      let result = [];
      if (isValidToQuery(val)) {
        dataIsAsync && setFetching(1);
        let resProcess;
        let res = [];
        switch (listType) {
          case 'google':
            return showGoogleResults({ isFirstCall: false });

          default:
            try {
              res =
                (dataIsAsync && data
                  ? await fetcher(data, val)
                  : data &&
                    data.filter(
                      (d) =>
                        (d.name || '')
                          .toLowerCase()
                          .indexOf(val.toLowerCase()) >= 0
                    )) || [];

              if (dataProcess) {
                resProcess = await dataProcess({ items: res, value: val });
              }
            } catch (err) {}
            result = resProcess || res;

            /**
             * Set the last item as "search with google" button
             * change the name of the button if the first item is a text
             * that is in the googleBtnNameList
             */
            if (withGoogleButton) {
              const dq = debouncedQuery.toLowerCase();
              const withAddress = googleBtnNameList.some((p) =>
                dq.startsWith(p)
              );
              const sbtn = {
                id: -1,
                slug: 'search-with-google',
                code: -1,
                type: 'google',
                buttonType: 'google',
                name: withAddress
                  ? 'Non hai trovato l’indirizzo che cercavi?'
                  : 'Non hai trovato la località che cercavi?',
                secondary_text: withAddress
                  ? 'Visualizza altri risultati per località o indirizzo'
                  : 'Visualizza altri risultati per località o indirizzo',
              };
              result.push(sbtn);
            }
            result[0] ? setHighlighted(result[0]) : setHighlighted({});
            setItems(result);
            break;
        }
        setFetching(0);
      } else if (previewData && !dataIsAsync) {
        result = data;
        setItems(result);
      }
    };

    debouncedQuery && fetchResults(debouncedQuery);
  }, [data, dataIsAsync, debouncedQuery]); // eslint-disable-line

  const nextHighlighted = () => {
    const index = items.indexOf(highlighted);
    index >= 0 && index < items.length - 1 && setHighlighted(items[index + 1]);
  };
  const prevHighlighted = () => {
    const index = items.indexOf(highlighted);
    index > 0 && setHighlighted(items[index - 1]);
  };

  const blurFromInput = () => {
    if (!preventBlurFromInput) {
      const { current } = elRef;
      const input = current && current.querySelector('input');
      input && input.blur();
    }
  };

  const handleOnFocus = (ev) => {
    setFocused(1);
    onFocus && onFocus(ev);
  };

  const handleKeyDown = (ev) => {
    const key = ev.which;
    if ([9, 27, 40, 38, 13].indexOf(key) >= 0) ev.preventDefault();

    switch (key) {
      case 27: // Esc
        return clear();
      case 40: // Arrow Down
        return nextHighlighted();
      case 38: // Arrow Up
        return prevHighlighted();
      case 13: // Enter
      case 9: // Tab
        let toSelect;
        if (items.length) {
          toSelect = highlighted || '';
        } else if (allowAny) {
          toSelect = debouncedQuery ? { name: debouncedQuery } : '';
        } else if (selected) {
          toSelect = selected;
        }

        if (toSelect) {
          blurFromInput();
          return setSelectedItem(toSelect);
        }
    }
  };

  const isSelected = (item) => item && selected && item.id === selected.id;
  const isHighlighted = (item) =>
    item && highlighted && item.id === highlighted.id;
  const isNested = (item) => item && item.level === 11 && !item.isNotChild;
  const isLastNested = (worthChecking, item) => {
    if (!worthChecking) {
      return null;
    }
    return item.isLastChild;
  };

  const inputValue =
    query !== null
      ? query
      : getNameSelected
      ? getNameSelected(selected)
      : getDisplayName(selected);

  const showGoogleResults = async ({ isFirstCall = false }) => {
    const myuuid = uuid ? uuid : uuidv4();
    const data = await getGoogleFromLHSuggestionsRaw(inputValue, myuuid, isFirstCall);
    if (data && Array.isArray(data)) {
      const dataList = data.map((d) => ({ ...d, id: d.place_id }));
      setListType('google');
      dataList[0] ? setHighlighted(dataList[0]) : setHighlighted({});
      setItems(dataList);
      /**
       * Give focus to the input field of the google results
       * after the results are loaded
       */
      // const { current } = elRef;
      // const input = current && current.querySelector('input');
      // input && input.focus();
    }
    setUiid(myuuid);
    setFetching(0);
    setLastClick(0);
  };

  const emptyStateResult =
    typeof emptyState === 'function'
      ? emptyState({ value: inputValue })
      : emptyState;
  const hasEmptyState =
    !isFetching &&
    query &&
    emptyStateResult &&
    isValidToQuery(query) &&
    !items.length &&
    debouncedQuery === query;

  const plhBackRes =
    typeof placeholderBack === 'function'
      ? placeholderBack({
          focused,
          highlighted,
          selected,
          highlightedName: getDisplayName(highlighted),
          value: inputValue,
        })
      : placeholderBack;
  const inputProps = {
    value: inputValue,
    type,
    placeholderBack: plhBackRes,
    showCloseIfQuery,
    onFocus: handleOnFocus,
    onChange: setQuery,
    onKeyDown: handleKeyDown,
    isValid: isValid || (() => inputValue && selected && selected.id),
    autoComplete: 'none', // https://bugs.chromium.org/p/chromium/issues/detail?id=370363#c7
    ...(shortOnFocus && focused && { size: 'm' }),
    ...props,
  };

  const itemCls = (item) =>
    makeClassName([
      cls[`${mainCls}__item`],
      'tp-w--m tp-s--l bb--s is-clickable',
      itemClassName,
      isSelected(item) ? cls[`${mainCls}__item--sel`] : 0,
      isHighlighted(item) && item.buttonType === 'google' && cls[`${mainCls}__cnt-google__item-inn--hl`],
      isHighlighted(item) && flatSelectedEffect && item.buttonType !== 'google'
        ? cls[`${mainCls}__item--hl`]
        : 0,
    ]);
  const itemInnCls = (item) =>
    makeClassName([
      cls[`${mainCls}__item-inn`],
      isSelected(item) ? cls[`${mainCls}__item-inn--sel`] : 0,
      isHighlighted(item) && item.buttonType === 'google' && cls[`${mainCls}__google_txt--hl`],
      isHighlighted(item) && !flatSelectedEffect && item.buttonType !== 'google'
        ? cls[`${mainCls}__item-inn--hl`]
        : 0,
      isNested(item) ? 'autocmp-field__item-inn--l11' : 0,
      isLastNested(isNested(item), item) ? 'lst-l11' : 0,
    ]);

  const highlightValue = (str = '') => {
    const qr = query || '';
    const queryLow = qr.toLowerCase().trim();
    const strLow = str.toLowerCase();
    const start = strLow.indexOf(queryLow);
    const end = start + qr.length;
    return start >= 0 ? (
      <>
        {str.substr(0, start)}
        <span className={highlightedValueCls || 'tp-w--l c-txt--secondary'}>
          {str.substr(start, qr.length)}
        </span>
        {str.substr(end)}
      </>
    ) : (
      str
    );
  };

  let itemsHidden = items.length <= 0 && !hasEmptyState;
  if (previewData && !focused) {
    itemsHidden = true;
  }

  const cancelOn =
    (!!cancel && (!!(focused || query) || showCancel)) ||
    (showCloseIfQuery && query);

  const clsItems = makeClassName([
    cls[`${mainCls}__items`],
    noHeightLimit ? cls[`${mainCls}__items--no-h`] : 0,
    itemsHidden ? 'is-hidden' : 0,
  ]);
  const clsMain = makeClassName([
    cls[mainCls],
    themeItems && cls[`theme-${themeItems}`],
    className,
    hasEmptyState ? cls[`${mainCls}--empty`] : 0,
  ]);
  const itemsCls = makeClassName([
    itemsClassName,
    themeItems && cls[`theme-${themeItems}`],
    cls[`${mainCls}__items-wrap`],
    'c-br--f3 b-r--m c-bg--w',
    hasEmptyState ? cls[`${mainCls}__items-wrap--empty`] : 0,
    itemsAbs ? cls[`${mainCls}__items--abs`] : '',
  ]);
  const itemsEl = !itemsHidden && (
    <div className={itemsCls} onTouchStart={blurFromInput}>
      {preItems}
      <div className={clsItems}>
        {items.map((item = {}) => (
          // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
          <div
            key={item.id || item.place_id || item}
            className={itemCls(item)}
            onClick={(ev) => {
              // ev.nativeEvent.stopImmediatePropagation();
              setSelectedItem(item);
            }}
            onMouseOver={() => setHighlighted(item)}
            {...(isHighlighted(item) && { ref: highlightedEl })}
          >
            <div className={itemInnCls(item)}>              
              {children
                ? children(item, {
                    inputValue,
                    displayName: getDisplayName(item),
                    highlightValue,
                    textNode: highlightValue(getDisplayName(item)),
                    listType,
                    highlighted: isHighlighted(item),
                  })
                : highlightValue(getDisplayName(item))}
            </div>
          </div>
        ))}
        {hasEmptyState && (
          <div className={cls[`${mainCls}__empty-state`]}>
            {emptyStateResult}
          </div>
        )}
      </div>
      {listType === 'google' && !hasEmptyState && (
        <div className={cls[`${mainCls}__pwrd-google`]} />
      )}
      {postItems}
    </div>
  );

  return (
    <div ref={elRef} className={clsMain}>
      <Grid align="center">
        <GridItem>
          <InputField
            {...inputProps}
            className={cls[`${mainCls}__input`]}
            loading={!!isFetching}
          />
        </GridItem>
        <GridItem
          className={`${
            cls[`${mainCls}__canc-btn`]
          } is-clickable c-txt--f1 tp-s--s tp-w--m ${
            cancelOn ? cls[`${mainCls}__canc-btn--s`] : ''
          }`}
          behavior="fixed"
          onMouseDown={() => {
            clear();
            setFocused();
            typeof cancel === 'function' && cancel();
          }}
        >
          Annulla
        </GridItem>
      </Grid>
      {!!itemsEl &&
        (itemsTarget && itemsTarget.current
          ? createPortal(itemsEl, itemsTarget.current)
          : itemsEl)}
    </div>
  );
}

AutocompleteField.propTypes = {
  /**
   * Any possible value
   */
  value: PropTypes.any,

  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Class name to add to the single item of the list
   */
  itemClassName: PropTypes.string,

  /**
   * Class name to add to the whole the item list
   */
  itemsClassName: PropTypes.string,

  /**
   * Callback when the selection is changed
   */
  onChange: PropTypes.func,

  /**
   * Callback when the input is focused
   */
  onFocus: PropTypes.func,

  /**
   * Callback when the interaction with the autosuggest is ended (eg. item selected, autosuggest closed)
   */
  onEnd: PropTypes.func,

  /**
   * Callback when the highlighted item is changed
   */
  onHighlighted: PropTypes.func,

  /**
   * Enable Cancel button (visible on focus). Pass a function to use it as a callback
   */
  cancel: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),

  /**
   * Customize the name output for items/input
   */
  getDisplayName: PropTypes.func,

  /**
   * Customize the name for the selected item (printed in input)
   */
  getNameSelected: PropTypes.func,

  /**
   * Customize the selected item before sending it to onChange
   */
  itemToValue: PropTypes.func,

  /**
   * Custom method to fetch data. It takes as arguments the url and the value to search
   */
  fetcher: PropTypes.func,

  /**
   * Set the position of items as absolute
   */
  itemsAbs: PropTypes.bool,

  /**
   * Make the input shorter when focused
   */
  shortOnFocus: PropTypes.bool,

  /**
   * Pass string for an endpoint (eg. `https://api...`) or an array of data
   */
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  /**
   * Process data array before render. The function should return an array to take effect
   */
  dataProcess: PropTypes.func,

  /**
   * Empty state string
   */
  emptyState: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * Put content before the list of items
   */
  preItems: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Put content after the list of items
   */
  postItems: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Specify where to render the list of items
   */
  // itemsTarget: PropTypes.node,
  itemsTarget: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.shape({ current: PropTypes.any }),
  ]),

  /**
   * Check if the selected item is valid
   */
  isValid: PropTypes.func,

  /**
   * Remove height from the input
   */
  noHeightLimit: PropTypes.bool,

  /**
   * Show the data even with empty value. Useful with static array data
   */
  previewData: PropTypes.bool,

  /**
   * Accept values outside of suggestion list
   */
  allowAny: PropTypes.bool,

  /**
   * Accept values outside of suggestion list
   */
  showCancel: PropTypes.bool,

  /**
   * Debounce value to use before trigger API data requests
   */
  debounce: PropTypes.number,

  /**
   * Define the theme of items
   */
  themeItems: PropTypes.string,

  /**
   * Blur input and hide results on item selection
   */
  shouldClearOnSelect: PropTypes.bool,

  showCloseIfQuery: PropTypes.bool,

  highlightedValueCls: PropTypes.string,
  flatSelectedEffect: PropTypes.bool,
  preventBlurFromInput: PropTypes.bool,
  stopClickOutside: PropTypes.bool,
};

AutocompleteField.defaultProps = {
  type: 'text',
  value: '',
  className: '',
  itemClassName: '',
  itemsClassName: '',
  placeholderBack: '',
  onChange: () => {},
  onFocus: () => {},
  onEnd: () => {},
  onHighlighted: () => {},
  cancel: false,
  getDisplayName: (i) => i && i.name,
  getNameSelected: null,
  itemToValue: (i) => i,
  itemsAbs: false,
  shortOnFocus: false,
  data: [],
  dataProcess: null,
  emptyState: 'Nessun risultato',
  preItems: null,
  postItems: null,
  itemsTarget: null,
  isValid: null,
  noHeightLimit: false,
  previewData: false,
  allowAny: false,
  showCancel: false,
  debounce: 350,
  fetcher: (url, value) => fetch(`${url}?q=${value}`).then((r) => r.json()),
  themeItems: '',
  shouldClearOnSelect: true,
  showCloseIfQuery: false,
  highlightedValueCls: null,
  flatSelectedEffect: false,
  preventBlurFromInput: false,
  stopClickOutside: false,
};
